import {CommandKeybindingHint} from '@github-ui/ui-commands'
import {ActionList, ActionMenu} from '@primer/react'
import type {ComponentProps} from 'react'

export const firstOptionId = 'highlighted-line-menu-first-option'

export type CodeLineMenuAction = {
  id: string
  label: string
  commandId?: ComponentProps<typeof CommandKeybindingHint>['commandId']
} & (
  | {kind: 'local' | 'fixture'; onSelect: () => void; onClick?: () => void}
  | {kind: 'unavailable'; reason: string}
  | {kind: 'link'; href: string}
)

interface CodeLineMenuOptions {
  context: 'gutter' | 'source'
  singleLine: boolean
  onCopyLines: () => void
  permalink: string
  onCopyPermalink: () => void
  blameHref: string
  newIssuePath?: string | null
  newDiscussionPath?: string | null
  githubDevHref?: string
  sectionAction?: {collapsed: boolean; onSelect: () => void; unavailableReason?: string}
  onOpenRefSelector: () => void
}

export function createCodeLineMenuActions(options: CodeLineMenuOptions): CodeLineMenuAction[] {
  const actions: CodeLineMenuAction[] = [
    {
      id: 'copy-lines',
      label: options.singleLine ? 'Copy line' : 'Copy lines',
      kind: 'local',
      onSelect: options.onCopyLines,
    },
  ]
  if (options.permalink) {
    actions.push({id: 'copy-permalink', label: 'Copy permalink', kind: 'local', onSelect: options.onCopyPermalink})
  }
  actions.push({id: 'blame', label: 'View git blame', kind: 'link', href: options.blameHref})
  if (options.newIssuePath && options.permalink) {
    actions.push({
      id: 'issue',
      label: 'Reference in new issue',
      kind: 'link',
      href: `${options.newIssuePath}?permalink=${encodeURIComponent(options.permalink)}`,
    })
  }
  if (options.newDiscussionPath && options.permalink) {
    actions.push({
      id: 'discussion',
      label: 'Reference in new discussion',
      kind: 'link',
      href: `${options.newDiscussionPath}?permalink=${encodeURIComponent(options.permalink)}`,
    })
  }
  if (options.githubDevHref) {
    actions.push({id: 'github-dev', label: 'View file in GitHub.dev', kind: 'link', href: options.githubDevHref})
  }
  const section = options.sectionAction
  if (options.context === 'source' && options.singleLine && section) {
    const label = `${section.collapsed ? 'Expand' : 'Collapse'} current section`
    actions.push(
      section.unavailableReason === undefined
        ? {id: 'fold', label, kind: 'local', onSelect: section.onSelect}
        : {id: 'fold', label, kind: 'unavailable', reason: section.unavailableReason},
    )
  }
  actions.push({
    id: 'ref',
    label: 'View file in different branch/tag',
    kind: 'local',
    onSelect: options.onOpenRefSelector,
    commandId: 'code-view:open-ref-selector',
  })
  return actions
}

export function CodeLineMenu({
  actions,
  menuLabel,
  heading,
  overlayProps,
}: {
  actions: readonly CodeLineMenuAction[]
  menuLabel?: string
  heading?: string
  overlayProps?: Omit<ComponentProps<typeof ActionMenu.Overlay>, 'children'>
}) {
  const items = actions.map(action => {
    if (action.kind === 'link') {
      return (
        <ActionList.LinkItem key={action.id} href={action.href}>
          {action.label}
          {action.commandId && (
            <ActionList.TrailingVisual>
              <CommandKeybindingHint commandId={action.commandId} />
            </ActionList.TrailingVisual>
          )}
        </ActionList.LinkItem>
      )
    }
    const onClick = action.kind === 'unavailable' ? undefined : action.onClick
    return (
      <ActionList.Item
        key={action.id}
        className={action.id === 'copy-lines' ? firstOptionId : undefined}
        disabled={action.kind === 'unavailable'}
        inactiveText={action.kind === 'unavailable' ? action.reason : undefined}
        onSelect={action.kind === 'unavailable' ? undefined : action.onSelect}
        {...(onClick ? {onClick} : {})}
      >
        {action.label}
        {action.commandId && (
          <ActionList.TrailingVisual>
            <CommandKeybindingHint commandId={action.commandId} />
          </ActionList.TrailingVisual>
        )}
        {action.kind === 'fixture' && <ActionList.Description>Fixture intent only</ActionList.Description>}
      </ActionList.Item>
    )
  })
  return (
    <ActionMenu.Overlay width="small" {...overlayProps}>
      <ActionList
        data-testid="highlighted-line-menu"
        {...(menuLabel === undefined ? {} : {'aria-label': menuLabel, 'aria-labelledby': undefined})}
      >
        {heading === undefined ? (
          items
        ) : (
          <ActionList.Group>
            <ActionList.GroupHeading>{heading}</ActionList.GroupHeading>
            {items}
          </ActionList.Group>
        )}
      </ActionList>
    </ActionMenu.Overlay>
  )
}
